import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { PaperProps } from '@mui/material/Paper';
import Draggable from 'react-draggable';
import {
  CenteredSpaceBetweenRow,
  JustifyStartRow,
} from 'spw-shared/globalComponents/grid';
import { CustomDivider } from 'spw-components/floorplan/styles';
import { SPWIconButton } from 'spw-shared/globalComponents/buttons';
import { Box, Grid } from '@mui/material';
import {
  MarginLeft8,
  MarginTop16,
  SpaceTypeLabel,
} from 'spw-shared/globalComponents/containers';
import { IProjectSpaceTypeCategory } from 'spw-redux/modules/projects/types';
import {
  INDIVIDUAL_WORKSPACES_CATEGORY,
  MEETING_SPACES_CATEGORY,
  AMENITY_SPACES_CATEGORY,
  SUPPORT_SPACES_CATEGORY,
} from 'spw-constants';

import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { colors } from 'spw-theme';
import { Clear } from '@mui/icons-material';
import { DraggablePaper } from 'spw-shared/globalComponents/draggable-paper';
import { Switch } from 'spw-components/global/styles';
import { useSelector } from 'react-redux';
import { projectColors as selectProjectColors } from 'spw-redux/modules/projects/selectors';
import { getColor } from 'spw-shared/utils';
import useUnitConversion from 'spw-components/unit-conversion/hooks';
import { selectMeasurementSystem } from 'spw-redux/modules/user/selectors';
import { MeasurementSystem } from 'spw-components/unit-conversion/types';
import {
  DialogContentHeader,
  ColorDot,
  DialogHeader,
  DialogContentText,
  TipsButton,
  MutedDialogContentText,
} from './styles';

function PaperComponent(props: PaperProps) {
  return (
    <Draggable
      bounds="#react-container"
      handle="#draggable-dialog-title"
      cancel={'[class*="MuiDialogContent-root"]'}
      defaultPosition={{ x: 32, y: 32 }}
    >
      <DraggablePaper {...props} />
    </Draggable>
  );
}

const WorksheetTooltipModal = ({
  disableButton,
  projectSpaceTypeCategories,
}: {
  disableButton?: boolean;
  projectSpaceTypeCategories: IProjectSpaceTypeCategory[];
}) => {
  const { getUnit, getUnitName } = useUnitConversion();
  const measurementSystem = useSelector(selectMeasurementSystem);
  const [open, setOpen] = React.useState(false);
  const projectColors = useSelector(selectProjectColors);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const spaceTypeCategoriesMap: Record<
    string,
    IProjectSpaceTypeCategory
  > = projectSpaceTypeCategories.reduce(
    (map, projectSpaceTypeCategory: IProjectSpaceTypeCategory) => {
      return {
        ...map,
        [projectSpaceTypeCategory.defaultSpaceTypeCategory
          .category]: projectSpaceTypeCategory,
      };
    },
    {}
  );

  const dimensionsUnit = getUnit('linear', {
    outputIsFractionUnit: measurementSystem === MeasurementSystem.metric,
  });

  return (
    <>
      <TipsButton
        disabled={disableButton}
        startIcon={<InfoOutlined style={{ fontSize: '18px' }} />}
        onClick={handleClickOpen}
      >
        <span style={{ color: colors.white }}>Tips</span>
      </TipsButton>
      <Dialog
        open={open}
        PaperComponent={PaperComponent}
        aria-labelledby="draggable-dialog-title"
        style={{
          // Trick to prevent the modal backdrop from covering up backgroun items
          // While simultaneously keeping the position: fixed to avoid scroll position jumps
          width: 0,
          height: 0,
        }}
        disableEnforceFocus
        hideBackdrop
        disableScrollLock
        PaperProps={{
          style: {
            zIndex: 1001,
            width: '312px',
            left: '20px',
            top: '48px',
            maxHeight: '75%',
          },
        }}
      >
        <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
          <CenteredSpaceBetweenRow>
            <DialogHeader>Worksheet Tips</DialogHeader>
            <SPWIconButton onClick={handleClose}>
              <Clear color="primary" fontSize="small" />
            </SPWIconButton>
          </CenteredSpaceBetweenRow>
        </DialogTitle>
        <DialogContent style={{ padding: '8px 12px 8px 24px' }}>
          <DialogContentHeader>Unused Spaces</DialogContentHeader>
          <DialogContentText>
            Hidden spaces can be added back to any worksheet within a project at
            any time
          </DialogContentText>
          <MutedDialogContentText>
            TIP: Only custom spaces can be deleted
          </MutedDialogContentText>

          <MarginTop16 />

          <CustomDivider />

          <MarginTop16 />
          <DialogContentHeader>Custom Spaces</DialogContentHeader>
          <DialogContentText>
            Duplicate space names cannot be created within a project
            <br />
            <br />
            Custom spaces can however be added to any worksheet within a project
          </DialogContentText>

          <MarginTop16 />

          <CustomDivider />
          <MarginTop16 />

          <DialogContentHeader>
            {getUnit('area')} (USABLE SQUARE{' '}
            {getUnitName('main-unit').toUpperCase()})
          </DialogContentHeader>
          <DialogContentText>
            Enter {getUnit('area')} of a space when dimensions are not known or
            not applicable
          </DialogContentText>
          <MarginLeft8>
            <DialogContentHeader>- OR -</DialogContentHeader>
          </MarginLeft8>
          <DialogContentHeader $textTransform="none">
            DIMENSIONS (
            {measurementSystem === MeasurementSystem.imperial
              ? dimensionsUnit.toUpperCase()
              : dimensionsUnit}
            )
          </DialogContentHeader>
          <DialogContentText>
            Enter dimensions instead of {getUnit('area')} if preferred and{' '}
            {getUnit('area')} will automatically calculate
          </DialogContentText>
          <MarginTop16 />

          <CustomDivider />
          <MarginTop16 />

          <JustifyStartRow>
            <ColorDot
              color={getColor(INDIVIDUAL_WORKSPACES_CATEGORY, projectColors)}
              className="colorDot"
            />
            <SpaceTypeLabel>
              {spaceTypeCategoriesMap[INDIVIDUAL_WORKSPACES_CATEGORY]?.name}
            </SpaceTypeLabel>
          </JustifyStartRow>
          <DialogContentHeader>
            Seat % MIX (OF TOTAL WORK SEATS)
          </DialogContentHeader>
          <DialogContentText>
            Enter % allocation of work seats per workspace type to calculate
            quantity
          </DialogContentText>
          <MutedDialogContentText>
            TIP: Total SEAT % MIX should equal 100%
          </MutedDialogContentText>
          <MarginLeft8>
            <DialogContentHeader>- OR -</DialogContentHeader>
          </MarginLeft8>
          <DialogContentHeader>QTY (Quantity)</DialogContentHeader>
          <DialogContentText>
            Enter quantity per workspace type to calculate Seat % Mix
          </DialogContentText>
          <MutedDialogContentText>
            TIP: Work Seats Remaining is a guide to ensure total QTY does not
            use more work seats than Total Work Seats available
          </MutedDialogContentText>
          <MarginTop16 />

          <CustomDivider />
          <MarginTop16>
            <Grid container>
              <Grid item xs={12}>
                <JustifyStartRow>
                  <ColorDot
                    color={getColor(MEETING_SPACES_CATEGORY, projectColors)}
                    className="colorDot"
                  />
                  <SpaceTypeLabel>
                    {spaceTypeCategoriesMap[MEETING_SPACES_CATEGORY]?.name}
                  </SpaceTypeLabel>
                </JustifyStartRow>
              </Grid>
            </Grid>
          </MarginTop16>
          <MarginTop16>
            <Grid item xs={12}>
              <JustifyStartRow>
                <ColorDot
                  color={getColor(AMENITY_SPACES_CATEGORY, projectColors)}
                  className="colorDot"
                />
                <SpaceTypeLabel>
                  {spaceTypeCategoriesMap[AMENITY_SPACES_CATEGORY]?.name}
                </SpaceTypeLabel>
              </JustifyStartRow>
            </Grid>
          </MarginTop16>
          <MarginTop16>
            <Grid container>
              <Grid item>
                <JustifyStartRow>
                  <ColorDot
                    color={getColor(SUPPORT_SPACES_CATEGORY, projectColors)}
                    className="colorDot"
                  />
                  <SpaceTypeLabel>
                    {spaceTypeCategoriesMap[SUPPORT_SPACES_CATEGORY]?.name}
                  </SpaceTypeLabel>
                </JustifyStartRow>
              </Grid>
            </Grid>
          </MarginTop16>

          <DialogContentHeader>TARGET RATIO</DialogContentHeader>
          <DialogContentText>
            Enter the target ratio per space type to calculate quantity of
            spaces{' '}
          </DialogContentText>
          <MutedDialogContentText>
            Total Work Seats / Target Ratio = Qty
          </MutedDialogContentText>
          <MarginLeft8>
            <DialogContentHeader>- OR -</DialogContentHeader>
          </MarginLeft8>
          <DialogContentHeader>QTY (Quantity)</DialogContentHeader>

          <DialogContentText>
            Enter the quantity per space type to calculate target ratios{' '}
          </DialogContentText>
          <MutedDialogContentText>
            Total Work Seats / Qty = Target Ratio{' '}
          </MutedDialogContentText>
          <MarginTop16 />
          <CustomDivider />
          <DialogContentHeader>
            <Box marginLeft={-1.5} marginTop={-1} marginBottom={-1}>
              <Switch className="no-pointer" checked color="default" />
            </Box>
            <Box>DECIMALS</Box>
          </DialogContentHeader>
          <DialogContentText>Hide/show fractional values</DialogContentText>
          <MutedDialogContentText>
            TIP: When using target ratios and seat mix as a guide for your
            inputs, manually update fractional qtys to ensure whole numbers in
            your program
          </MutedDialogContentText>
          <MarginTop16 />
          <CustomDivider />
          <DialogContentHeader>
            <Box marginLeft={-1.5} marginTop={-1} marginBottom={-1}>
              <Switch className="no-pointer" checked color="default" />
            </Box>
            <Box>CLIENT NAMES</Box>
          </DialogContentHeader>
          <DialogContentText>
            View/edit client facing space type names
          </DialogContentText>
          <MutedDialogContentText>
            TIP: When possible, use the pre-loaded names and rename them instead
            of creating new custom space type names
          </MutedDialogContentText>
          <MarginTop16 />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default WorksheetTooltipModal;
